import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';

import { SubActions } from 'Redux/thunks/subscriptions.thunks';

import SesamModal from 'Common/SesamModal/SesamModal';
import CollapsePanel from 'Common/CollapsePanel/CollapsePanel';
import { ConnectPanel } from '../components/ConnectPanel/ConnectPanel.view';
import { ConnectionStatus } from '../components/ConnectPanel/ConnectionStatus.view';

import CodeViewer from '../components/code-viewer';
import NetworkAcl from '../components/network-acl';
import ConnectHelp from '../subscription/components/connect-help';

import { persistURLService, canConnect } from '../services/connectionString.service';
import { CustomConnectionsThunk } from 'Redux/thunks/customConnectionURL.thunks';
import { getConnectionString } from '../utils/subscription.utils';
import { preLoadCoreData } from '../internals/onEnterFunctions';

import type { RouteComponentProps } from 'react-router';
import type { RootState } from '../types/state.types';

import { TestID } from '../testID';

const getDefaultConnectionString = (subscriptionId: string, connected: boolean) => {
  return connected ? getConnectionString(subscriptionId) : undefined;
};

export const SettingsSubscriptionNetwork: React.FC<RouteComponentProps<{}, any>> = ({
  location,
}) => {
  const dispatch = useDispatch();
  const subscription = useSelector((state: RootState) => state.subscription);
  const subscriptionId = subscription?.id || '';
  const initialPersistedURL = useSelector((state: RootState) =>
    getDefaultConnectionString(state.subscription?.id, state.subscription?.connected)
  );
  const defaultConnectionUrl = subscription.defaultConnectionUrl;
  const url = subscription?.url;
  const lastConnectFailed = subscription?.lastConnectFailed;
  const lastConnectUrl = subscription?.lastConnectUrl;
  const connected = subscription?.connected;
  const connectLog = subscription.connectLog;
  const currentSubscription = useSelector((state: RootState) =>
    state.subscriptions.find((s) => s.id === subscriptionId)
  );
  const serviceType = currentSubscription?.service ?? 'in-cloud';
  const provisionerVersion = currentSubscription?.provisioner_version;

  const [couldNotConnect, setCouldNotConnect] = useState(false);
  const [disableCheckbox, setDisableCheckbox] = useState(false);
  const [couldNotConnectDefault, setCouldNotConnectDefault] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [shouldPersistURL, setShouldPersistURL] = useState(false);
  const [persistedURL, setPersistedURL] = useState(initialPersistedURL ?? '');
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [pleaseWait, setPleaseWait] = useState(false);
  const [notValid, setNotValid] = useState(false);
  const [stored, setStored] = useState('');
  const [storing, setStoring] = useState(false);

  const storeUrl = async (id: string, url: string) => dispatch(SubActions.storeUrl(id, url));

  const attemptConnect = async (url: string) => dispatch(SubActions.attemptConnect(url));

  const setCustomConnectionURL = (subscriptionId: string, persistedURL?: string) =>
    dispatch(CustomConnectionsThunk.setCustomConnection(subscriptionId, persistedURL ?? ''));

  const removeCustomConnectionURL = (subscriptionId: string) =>
    dispatch(CustomConnectionsThunk.removeCustomConnection(subscriptionId));

  const restoreDefaultConnectionString = (subscriptionId: string, defaultConnection: string) =>
    dispatch(SubActions.attemptConnectV2(subscriptionId, defaultConnection)).then(() => {
      dispatch(CustomConnectionsThunk.removeCustomConnection(subscriptionId));
    });

  const disconnect = () => dispatch(SubActions.disconnect());

  const handleOpenFailedModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowFailedModal(true);
  };

  const connectDefault = () => {
    setPleaseWait(true);
    setCouldNotConnectDefault(false);

    if (defaultConnectionUrl) {
      attemptConnect(defaultConnectionUrl)
        .then(() => setPleaseWait(false))
        .catch(() => {
          setPleaseWait(false);
          setCouldNotConnectDefault(true);
        });
    }
  };

  const connectToApi = (e: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const isValid = !currentUrl;
    setNotValid(isValid);

    if (!isValid) {
      setPleaseWait(true);
      setCouldNotConnect(false);
      setCouldNotConnectDefault(false);
      setStored('');
      setDisableCheckbox(true);

      attemptConnect(persistedURL || currentUrl)
        .then(async (result: string) => {
          if (result === 'okay') {
            setPleaseWait(false);
            setCouldNotConnect(false);

            if (persistedURL) {
              setCustomConnectionURL(subscriptionId, persistedURL);

              if (shouldPersistURL) {
                persistURLService().setPersistedURL(subscriptionId, persistedURL);
                setDisableCheckbox(false);
              }
            } else {
              removeCustomConnectionURL(subscriptionId);
            }

            await preLoadCoreData();
          }
        })
        .catch(() => {
          setPleaseWait(false);
          setCouldNotConnect(true);

          restoreDefaultConnectionString(subscriptionId, defaultConnectionUrl);
        });
    }
  };

  const setUrlAsDefault = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const confirmed = confirm(`Are you sure you want to set "${currentUrl}" as default?`);

    if (confirmed && subscriptionId && currentUrl) {
      const url = currentUrl;

      setStored('');
      setStoring(true);
      setCouldNotConnect(false);

      storeUrl(subscriptionId, url)
        .then(() => {
          setStored(url);
          setStoring(true);
        })
        .catch(() => {
          setStoring(false);
        });
    }
  };

  const possibleToConnect = () =>
    canConnect({
      pleaseWait: pleaseWait,
      previousURL: url,
      nextURL: currentUrl,
      persistedURL: persistedURL,
    });

  const canSetDefault = () => !storing && !!currentUrl && defaultConnectionUrl !== currentUrl;

  const handleFieldChange = (ev: React.InputEvent<HTMLInputElement>) => {
    const value = (ev.target as HTMLInputElement).value;

    if (value !== defaultConnectionUrl) {
      setPersistedURL(value);
      setDisableCheckbox(false);
    } else {
      setShouldPersistURL(false);
      setPersistedURL('');
      setDisableCheckbox(true);
    }

    setCurrentUrl(value);
  };

  const persistedURLCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (!isChecked && subscriptionId) {
      removeCustomConnectionURL(subscriptionId);
      setPersistedURL('');
      setShouldPersistURL(false);
    } else {
      setShouldPersistURL(true);
    }
  };

  useEffect(() => {
    const asQueryParam = location?.query?.connection_url;

    if (!persistedURL && !asQueryParam) {
      setShouldPersistURL(false);
      setPersistedURL('');
    } else {
      if (asQueryParam) {
        setShouldPersistURL(true);
        setPersistedURL(asQueryParam);
      }
    }
  }, []);

  useEffect(() => {
    const persistedURLFromLocalStorage = getConnectionString(subscriptionId);

    setCurrentUrl(persistedURLFromLocalStorage || url);

    if (!persistedURLFromLocalStorage && url) {
      setDisableCheckbox(true);
    }
  }, []);

  return (
    <main className="scrollArea">
      <div className="row">
        <div className="col gr-equal">
          <h2 className="heading-section">Connection</h2>
          <ConnectionStatus
            couldNotConnectDefault={couldNotConnectDefault}
            defaultUrl={defaultConnectionUrl}
            disconnect={disconnect}
            connected={connected}
            connectDefault={connectDefault}
            handleOpenFailedModal={handleOpenFailedModal}
            pleaseWait={pleaseWait}
            publicIp={get(subscription, 'provisioning_result.datahub.public_ip') ?? ''}
            privateIp={get(subscription, 'provisioning_result.datahub.private-ip') ?? ''}
            url={url}
          />
          <ConnectPanel
            canConnect={possibleToConnect()}
            canSetDefault={canSetDefault()}
            connect={connectToApi}
            couldNotConnect={couldNotConnect}
            disableCheckbox={disableCheckbox}
            handleFieldChange={handleFieldChange}
            handleOpenFailedModal={handleOpenFailedModal}
            notValid={notValid}
            persistedURL={persistedURL}
            persistedURLCheckbox={persistedURLCheckbox}
            pleaseWait={pleaseWait}
            serviceType={serviceType}
            shouldPersistURL={shouldPersistURL}
            stored={stored}
            setUrlAsDefault={setUrlAsDefault}
            storing={storing}
            url={currentUrl || ''}
          />
          <CollapsePanel title="Connection log">
            <CodeViewer code={JSON.stringify(connectLog, null, 2)} />
          </CollapsePanel>
        </div>
        {provisionerVersion !== 'snowflake' && (
          // this.props.can_modify_subscription.enabled && (
          <div className="col gr-equal">
            <NetworkAcl />
          </div>
        )}
      </div>
      <SesamModal
        className="simple-dialog"
        isOpen={showFailedModal}
        onRequestClose={() => setShowFailedModal(false)}
        contentLabel="Connection failed"
        testid={TestID.ConnectionFailedModal}
      >
        <ConnectHelp failed={lastConnectFailed} lastUrl={lastConnectUrl} subId={subscriptionId} />
      </SesamModal>
    </main>
  );
};
